// AdminUnknownUsersManager.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Divider,
  Grid,
  Tooltip,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from '../../../config/api';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import safeToast from 'utils/safeToast';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminUnknownUsersManager = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchRegistrations = useCallback(async (status = null) => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.UNKNOWN_ALUMNI.GET_LIST(status));
      const data = Array.isArray(response.data) ? response.data : [];
      setRowData(data);
    } catch (error) {
      console.error('Error fetching unknown alumni registrations:', error);
      safeToast.error('Failed to load registrations');
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
    }
  }, [open, statusFilter, fetchRegistrations]);

  const actionCellRenderer = useCallback((params) => {
    if (params.data.status !== 'Pending') {
      return (
        <Chip
          label={params.data.status}
          color={params.data.status === 'Approved' ? 'success' : 'error'}
          size="small"
          sx={{ fontSize: '0.7rem', height: '20px', '& .MuiChip-label': { padding: '0 6px' } }}
        />
      );
    }

    return (
      <Box display="flex" gap={0.5} alignItems="center">
        <Tooltip title="View Details">
          <IconButton
            size="small"
            color="info"
            onClick={() => {
              setSelectedRegistration(params.data);
              setDetailsDialogOpen(true);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Approve">
          <IconButton
            size="small"
            color="success"
            onClick={() => {
              setSelectedRegistration(params.data);
              setApproveDialogOpen(true);
            }}
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reject">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedRegistration(params.data);
              setRejectDialogOpen(true);
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 80,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      cellStyle: { textAlign: 'center', fontWeight: 600, fontSize: '0.75rem' },
    },
    {
      field: "fullName",
      headerName: "Name",
      width: 200,
      cellStyle: { fontWeight: 500, fontSize: '0.75rem' },
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 120,
    },
    {
      field: "whatsAppNumber",
      headerName: "WhatsApp",
      width: 120,
    },
    {
      field: "batchYear",
      headerName: "Batch",
      width: 100,
    },
    {
      field: "collegeStudied",
      headerName: "College",
      width: 200,
    },
    {
      field: "courseStudied",
      headerName: "Course",
      width: 150,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      cellRenderer: (params) => {
        const status = params.value;
        const color = status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : 'warning';
        return (
          <Chip
            label={status}
            color={color}
            size="small"
            sx={{ fontSize: '0.7rem', height: '20px', '& .MuiChip-label': { padding: '0 6px' } }}
          />
        );
      },
    },
    {
      field: "createdDate",
      headerName: "Submitted Date",
      width: 150,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      },
      filterValueGetter: (params) => {
        if (!params.data?.createdDate) return '';
        return new Date(params.data.createdDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      },
    },
    {
      headerName: "Actions",
      width: 150,
      cellRenderer: actionCellRenderer,
      sortable: false,
      filter: false,
    },
  ], [actionCellRenderer]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
    cellStyle: { fontSize: '0.75rem' },
    headerClass: 'small-header',
  }), []);

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  const handleApprove = useCallback(async () => {
    if (!selectedRegistration) return;

    setProcessing(true);
    try {
      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.VERIFY, {
        registrationId: selectedRegistration.registrationId,
        action: 'Approve',
        verifiedBy: 1, // TODO: Get from auth context
        remarks: remarks,
      });

      if (response.data?.success) {
        safeToast.success(`Registration approved successfully! ${response.data.studentId ? `StudentId: ${response.data.studentId}` : ''}`);
        setApproveDialogOpen(false);
        setRemarks('');
        setSelectedRegistration(null);
        fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
      } else {
        throw new Error(response.data?.message || 'Failed to approve registration');
      }
    } catch (error) {
      console.error('Error approving registration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve registration';
      safeToast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedRegistration, remarks, statusFilter, fetchRegistrations]);

  const handleReject = useCallback(async () => {
    if (!selectedRegistration || !rejectionReason.trim()) {
      safeToast.warn('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.VERIFY, {
        registrationId: selectedRegistration.registrationId,
        action: 'Reject',
        verifiedBy: 1, // TODO: Get from auth context
        rejectionReason: rejectionReason,
        remarks: remarks,
      });

      if (response.data?.success) {
        safeToast.success('Registration rejected successfully');
        setRejectDialogOpen(false);
        setRejectionReason('');
        setRemarks('');
        setSelectedRegistration(null);
        fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
      } else {
        throw new Error(response.data?.message || 'Failed to reject registration');
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject registration';
      safeToast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedRegistration, rejectionReason, remarks, statusFilter, fetchRegistrations]);

  const stats = useMemo(() => {
    const total = rowData.length;
    const pending = rowData.filter(r => r.status === 'Pending').length;
    const approved = rowData.filter(r => r.status === 'Approved').length;
    const rejected = rowData.filter(r => r.status === 'Rejected').length;
    return { total, pending, approved, rejected };
  }, [rowData]);

  if (loading && rowData.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress size={50} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl" 
      fullScreen
      PaperProps={{
        sx: {
          background: '#ffffff',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          px: 3,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            Unknown Users Management
          </MDTypography>
          <IconButton
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <MDBox display="flex" flexDirection="column" gap={2}>
          {/* Statistics */}
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
              <MDBox>
                <MDTypography variant="caption" color="text">Total:</MDTypography>
                <MDTypography variant="h6" fontWeight="bold">{stats.total}</MDTypography>
              </MDBox>
              <Divider orientation="vertical" flexItem />
              <MDBox>
                <MDTypography variant="caption" color="warning">Pending:</MDTypography>
                <MDTypography variant="h6" fontWeight="bold" color="warning">{stats.pending}</MDTypography>
              </MDBox>
              <Divider orientation="vertical" flexItem />
              <MDBox>
                <MDTypography variant="caption" color="success">Approved:</MDTypography>
                <MDTypography variant="h6" fontWeight="bold" color="success">{stats.approved}</MDTypography>
              </MDBox>
              <Divider orientation="vertical" flexItem />
              <MDBox>
                <MDTypography variant="caption" color="error">Rejected:</MDTypography>
                <MDTypography variant="h6" fontWeight="bold" color="error">{stats.rejected}</MDTypography>
              </MDBox>
            </MDBox>
          </Paper>

          {/* Filters */}
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Autocomplete
                size="small"
                sx={{ minWidth: 200 }}
                options={['All', 'Pending', 'Approved', 'Rejected']}
                value={statusFilter}
                onChange={(event, newValue) => {
                  if (newValue) setStatusFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Status" />
                )}
              />
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => fetchRegistrations(statusFilter === 'All' ? null : statusFilter)}
              >
                Refresh
              </MDButton>
            </MDBox>
          </Paper>

          {/* Data Grid */}
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 300px)', width: '100%' }}>
              <style>{`
                .ag-theme-alpine .small-header {
                  font-size: 0.75rem !important;
                  font-weight: 600;
                }
                .ag-theme-alpine .ag-header-cell-text {
                  font-size: 0.75rem !important;
                }
                .ag-theme-alpine .ag-cell {
                  font-size: 0.75rem !important;
                  line-height: 1.2;
                }
                .ag-theme-alpine .ag-floating-filter-input {
                  font-size: 0.75rem !important;
                }
              `}</style>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                animateRows={true}
                rowHeight={32}
                headerHeight={32}
                pagination={true}
                paginationPageSize={25}
              />
            </div>
          </Paper>
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <MDButton onClick={onClose} color="dark" variant="outlined" size="small">
          Close
        </MDButton>
      </DialogActions>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => !processing && setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Registration</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Are you sure you want to approve this registration? The user will be moved to the Alumni database.
          </MDTypography>
          {selectedRegistration && (
            <MDTypography variant="body2" fontWeight="bold" color="dark">
              {selectedRegistration.fullName} ({selectedRegistration.email || selectedRegistration.mobile})
            </MDTypography>
          )}
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleApprove} color="success" variant="contained" disabled={processing}>
            {processing ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => !processing && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this registration.
          </MDTypography>
          {selectedRegistration && (
            <MDTypography variant="body2" fontWeight="bold" color="dark" sx={{ mb: 2 }}>
              {selectedRegistration.fullName} ({selectedRegistration.email || selectedRegistration.mobile})
            </MDTypography>
          )}
          <TextField
            fullWidth
            label="Rejection Reason *"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            error={!rejectionReason.trim()}
            helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            color="error" 
            variant="contained" 
            disabled={processing || !rejectionReason.trim()}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Registration Details
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRegistration && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  {selectedRegistration.fullName}
                </MDTypography>
                <Chip
                  label={selectedRegistration.status}
                  color={selectedRegistration.status === 'Approved' ? 'success' : selectedRegistration.status === 'Rejected' ? 'error' : 'warning'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Email:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.email || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Mobile:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.mobile || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">WhatsApp:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.whatsAppNumber || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Batch Year:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.batchYear || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">College:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.collegeStudied || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Course:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.courseStudied || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Branch:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.branch || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Organization:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.organization || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Designation:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.designation || 'N/A'}</MDTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDTypography variant="caption" color="text">Location:</MDTypography>
                <MDTypography variant="body2">{selectedRegistration.location || 'N/A'}</MDTypography>
              </Grid>
              {selectedRegistration.feedback && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text">Feedback:</MDTypography>
                  <MDTypography variant="body2">{selectedRegistration.feedback}</MDTypography>
                </Grid>
              )}
              {selectedRegistration.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <MDTypography variant="caption" fontWeight="bold">Rejection Reason:</MDTypography>
                    <MDTypography variant="body2">{selectedRegistration.rejectionReason}</MDTypography>
                  </Alert>
                </Grid>
              )}
              {selectedRegistration.remarks && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text">Remarks:</MDTypography>
                  <MDTypography variant="body2">{selectedRegistration.remarks}</MDTypography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

AdminUnknownUsersManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminUnknownUsersManager;

